import { AmountCell, NameCell, NoData } from "@";
import {
  financialSupervisorColumnsA,
  financialSupervisorColumnsB,
  financialSupervisorColumnsC,
  financialSupervisorColumnsD,
} from "../utils/options";

// Reusable refined table primitives.
// Cells now wrap the shared AmountCell/NameCell, so td shells just provide
// spacing — typography lives inside the cell components.
const tableContainerClass =
  "relative overflow-x-auto rounded-2xl border border-gray-100 bg-white dark:border-gray-700/60 dark:bg-gray-800 print:rounded-none print:border-0";
const theadRowClass =
  "border-b border-gray-100 bg-gradient-to-l from-purple-50/30 via-gray-50/70 to-gray-50/40 dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-900/60 dark:to-gray-900/40";
const thClass =
  "whitespace-nowrap px-5 py-3.5 text-start text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400";
const tdClass = "px-5 py-3.5";
const trZebraClass =
  "border-b border-gray-100 transition-colors last:border-0 hover:bg-purple-50/30 dark:border-gray-700/40 dark:hover:bg-purple-950/15";

// Tiny inline label that marks an "إهداء" (gift) project row inside the
// project-donations tab. Stays here because it's specific to that tab.
const GiftBadge = () => (
  <span className="me-2 inline-flex items-center rounded-full bg-purple-50 px-1.5 py-0 text-[10px] font-bold text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
    إهداء
  </span>
);

const TabPanel = ({ id, printOnlyTitle, children }) => (
  <div
    className="hidden p-0 print:mb-8 print:block print:p-4 print:shadow-none"
    id={id}
    role="tabpanel"
    aria-labelledby={`${id}-tab`}
  >
    {printOnlyTitle && (
      <h3 className="hidden print:mb-4 print:block">{printOnlyTitle}</h3>
    )}
    {children}
  </div>
);

const TabsContent = ({ data }) => {
  return (
    <div id="tab-financial">
      {/* Total */}
      <TabPanel id="tabA" printOnlyTitle="الاجمالي">
        <div className={tableContainerClass}>
          <table className="w-full text-start">
            <thead className={theadRowClass}>
              <tr>
                {financialSupervisorColumnsA.map((item, i) => (
                  <th key={i} scope="col" className={thClass}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gradient-to-l from-purple-50/40 via-gray-50/60 to-transparent dark:from-purple-950/30 dark:via-gray-900/40">
                <td className={tdClass}>
                  <AmountCell
                    value={data?.totalRevenue?.cardPayment}
                    tone="purple"
                  />
                </td>
                <td className={tdClass}>
                  <AmountCell
                    value={data?.totalRevenue?.bankTransfer}
                    tone="purple"
                  />
                </td>
                <td className={tdClass}>
                  <AmountCell
                    value={data?.totalRevenue?.recurringPayment}
                    tone="purple"
                  />
                </td>
                <td className={tdClass}>
                  <AmountCell value={data?.totalRevenue?.total} tone="purple" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>

      {/* Project donations */}
      <TabPanel id="tabB" printOnlyTitle="تبرعات المشاريع">
        <div className={tableContainerClass}>
          <table className="w-full text-start">
            <thead className={theadRowClass}>
              <tr>
                {financialSupervisorColumnsB.map((item, i) => (
                  <th key={i} scope="col" className={thClass}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.projectRevenues?.map((item, i) => (
                <tr key={`p-${i}`} className={trZebraClass}>
                  <td className={tdClass}>
                    <NameCell name={item.projectTitle} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.cardPayment} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.bankTransfer} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.total} tone="purple" />
                  </td>
                </tr>
              ))}
              {data?.giftProjectRevenues?.map((item, i) => (
                <tr key={`g-${i}`} className={trZebraClass}>
                  <td className={tdClass}>
                    <GiftBadge />
                    <NameCell name={item.giftProjectTitle} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.cardPayment} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.bankTransfer} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.total} tone="purple" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data?.projectRevenues?.length === 0 &&
          data?.giftProjectRevenues?.length === 0 && <NoData />}
      </TabPanel>

      {/* Fast donations */}
      <TabPanel id="tabC" printOnlyTitle="تبرع سريع">
        <div className={tableContainerClass}>
          <table className="w-full text-start">
            <thead className={theadRowClass}>
              <tr>
                {financialSupervisorColumnsC.map((item, i) => (
                  <th key={i} scope="col" className={thClass}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.fastDonationRevenues?.map((item, i) => (
                <tr key={`f-${i}`} className={trZebraClass}>
                  <td className={tdClass}>
                    <NameCell name={item.classificationTitle} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.cardPayment} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.bankTransfer} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.total} tone="purple" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data?.fastDonationRevenues?.length === 0 && <NoData />}
      </TabPanel>

      {/* Recurring payments */}
      <TabPanel id="tabD" printOnlyTitle="مدفوعات الاستقطاع">
        <div className={tableContainerClass}>
          <table className="w-full text-start">
            <thead className={theadRowClass}>
              <tr>
                {financialSupervisorColumnsD.map((item, i) => (
                  <th key={i} scope="col" className={thClass}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.recurringPayments?.map((item, i) => (
                <tr key={`r-${i}`} className={trZebraClass}>
                  <td className={tdClass}>
                    <NameCell name={item.projectTitle} />
                  </td>
                  <td className={tdClass}>
                    <AmountCell value={item.payment} tone="purple" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data?.recurringPayments?.length === 0 && <NoData />}
      </TabPanel>
    </div>
  );
};

export default TabsContent;
